/**
 * Type-safe wrappers модуля файлов (presigned upload в MinIO).
 *
 * Backend (Sprint 2 Stage 1.2, живой в проде):
 *   POST /v1/files/presign — выдать presigned URL для загрузки
 *     body:    { kind, contentType, contentLength, path?, expiresIn? }
 *     ответ:   { fileId, url, method, key, headers?, expiresAt }
 *
 * Дальше файл грузится напрямую в MinIO по выданному URL (PUT) —
 * см. uploadToPresignedUrl. В заявку каталога услуг уходит `key`.
 */
import { customFetch } from './openapi-fetch.js';

/** POST /v1/files/presign — тело запроса (camelCase как в Go DTO). */
export interface PresignFileInput {
  /** Тип файла в политике модуля файлов (для фото заявок — 'photo'). */
  kind: string;
  contentType: string;
  contentLength: number;
  path?: string;
  expiresIn?: number;
}

/** POST /v1/files/presign — ответ. */
export interface PresignedUpload {
  fileId: string;
  url: string;
  method: string;
  key: string;
  headers?: Record<string, string>;
  expiresAt: string;
}

/** Выдаёт presigned URL для прямой загрузки файла в объектное хранилище. */
export async function presignFile(input: PresignFileInput): Promise<PresignedUpload> {
  return customFetch<PresignedUpload>({
    url: '/files/presign',
    method: 'POST',
    data: input,
  });
}

/**
 * Грузит файл по presigned URL (обычно PUT напрямую в MinIO, мимо API).
 * Заголовки из presign-ответа обязательны — подпись URL их учитывает.
 */
export async function uploadToPresignedUrl(upload: PresignedUpload, file: Blob): Promise<void> {
  const res = await fetch(upload.url, {
    method: upload.method !== '' ? upload.method : 'PUT',
    headers: upload.headers ?? {},
    body: file,
  });
  if (!res.ok) {
    throw new Error(`upload failed: ${res.status} ${res.statusText}`);
  }
}

/** POST /v1/files/upload — ответ серверного multipart-прокси. */
export interface UploadFileResult {
  fileId: string;
  /** Объектный ключ в MinIO — кладём в cover_url / photos[]. */
  key: string;
  bytes: number;
}

/**
 * Загружает файл серверным multipart-прокси (POST /v1/files/upload). API сам
 * стримит байты в MinIO по loopback и возвращает object-key. Используется
 * вместо presigned-PUT: в одно-хостовом проде MinIO слушает 127.0.0.1:9002
 * без публичного ingress, поэтому presigned-URL из браузера недоступны.
 * Авторизация (Bearer) и X-Tenant-Slug проставляются как в customFetch.
 */
export async function uploadFile(file: File, kind = 'photo'): Promise<UploadFileResult> {
  const fd = new FormData();
  fd.append('file', file, file.name);
  fd.append('kind', kind);
  return customFetch<UploadFileResult>({
    url: '/files/upload',
    method: 'POST',
    // FormData → fetch сам выставит multipart Content-Type с boundary.
    data: fd,
  });
}

/** Путь стриминг-загрузки object-key обратно через API (img src). */
export function fileDownloadUrl(key: string): string {
  return `/api/v1/public/files/${key}`;
}
